import { useState } from 'react';
import { OpsApiError } from '../api/client.ts';
import { useOrgServiceLevel, useServiceLevelCatalogue, useSetOrgServiceLevel } from '../api/useServiceLevels.ts';
import type { OrgServiceLevelView } from '../api/serviceLevels.ts';
import { ROLE_OPS_ADMIN, hasAnyOpsRole } from '../auth/opsRoles.ts';
import { useSession } from '../hooks/useSession.tsx';
import { useToast } from './Toast.tsx';
import { useT } from '../i18n/index.ts';
import { Button, Chip, ConfirmDialog, Panel, Select, Skeleton } from '../ui/index.ts';
import type { SelectOption } from '../ui/index.ts';

/**
 * Customer 360 service-level assignment (#1723/#1725) — the customer manager's
 * whole routing surface.
 *
 * A CM picks a level BY NAME and reads its customer-facing description. No
 * routing-config name, SKU summary, weight, retry order, or priority appears
 * here, and none can: the ops-api projection this reads strips
 * `routing_config_id` before the response leaves the server, so there is
 * nothing in the payload to leak — and a save failure is rendered from a local
 * per-code string rather than relayed from the server, so a gateway message
 * that happens to name a config cannot become the one leak on this surface.
 * This replaces the routing-config workspace the tab used to mount.
 *
 * `custom` is a third state, not "some level": an admin hand-assigned this org
 * a routing config outside the catalogue. A CM sees that read-only rather than
 * a control that would silently overwrite the admin's work; an ADMIN keeps the
 * selector, because moving such an org onto a level (or back to plain default)
 * is supported server-side and this is the only surface that can do it.
 */

/** Sentinel for "no level" — Radix Select rejects an empty option value. */
const DEFAULT_OPTION = 'default';

function selectionOf(view: OrgServiceLevelView | undefined): string {
  return view?.state === 'level' && view.level !== null ? view.level.id : DEFAULT_OPTION;
}

/**
 * A save failure is described by its code, never by the server's message.
 * Relaying `message` verbatim was the one path on this card that could put
 * gateway-authored text — which has already once named a routing config — in
 * front of a customer manager.
 */
type MessageKey = Parameters<ReturnType<typeof useT>>[0];

function saveErrorKey(cause: unknown): MessageKey {
  if (!(cause instanceof OpsApiError)) return 'customer_manager_360.service_level.save_error.generic';
  if (cause.code === 'SERVICE_LEVEL_ARCHIVED') {
    return 'customer_manager_360.service_level.save_error.archived';
  }
  if (cause.code === 'SERVICE_LEVEL_NOT_FOUND' || cause.code === 'ROUTE_BINDING_NOT_FOUND') {
    return 'customer_manager_360.service_level.save_error.missing';
  }
  if (cause.code === 'SERVICE_LEVEL_ORG_ADMIN_MANAGED') {
    return 'customer_manager_360.service_level.save_error.admin_managed';
  }
  if (cause.status === 403 || cause.code === 'FORBIDDEN') {
    return 'customer_manager_360.service_level.save_error.forbidden';
  }
  return 'customer_manager_360.service_level.save_error.generic';
}

export function CustomerServiceLevelCard({
  orgId,
  testId,
}: {
  readonly orgId: string;
  readonly testId: string;
}): JSX.Element {
  const t = useT();
  const toast = useToast();
  const { user } = useSession();
  const isAdmin = hasAnyOpsRole(user?.roles ?? [], [ROLE_OPS_ADMIN]);
  const assignment = useOrgServiceLevel(orgId);
  const catalogue = useServiceLevelCatalogue();
  const setMut = useSetOrgServiceLevel();

  const current = selectionOf(assignment.data);
  // `null` means "follow the server". Deriving the shown value instead of
  // mirroring `current` into state keeps the control and the "currently:" line
  // from disagreeing for a commit, and makes dropping back to the server value
  // a single assignment rather than a re-sync effect.
  const [draft, setDraft] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Declared above the early returns so the hook order is stable across the
  // loading / error / read-only branches below.
  const [confirmClear, setConfirmClear] = useState(false);
  const selection = draft ?? current;

  // The catalogue is in BOTH gates on purpose. Folding a failed catalogue into
  // an empty one would show a customer who sits on an active level as sitting
  // on an archived one, with "Default" as the only selectable option — one
  // click from silently unassigning them. Loading it alongside the assignment
  // also stops that same wrong frame from being rendered while it is in flight.
  if (assignment.isLoading || catalogue.isLoading) {
    return (
      <Panel caption={t('customer_manager_360.service_level.caption')} padded testId={testId}>
        <div data-testid={`${testId}-loading`} className="flex flex-col gap-3">
          <Skeleton className="h-9 w-full max-w-sm" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      </Panel>
    );
  }

  // Gate on ABSENT data, not on `error`. Every successful save invalidates the
  // `['service-levels']` prefix, so the catalogue is refetched in the
  // background constantly; a failed background refetch sets `error` while
  // KEEPING the last good `data`, and gating on `error` would replace a working
  // card with an error box the moment after a save succeeded. Missing data is
  // the only condition that actually makes this card unrenderable — and it is
  // still the first-load failure H1 was about.
  if (assignment.data === undefined || catalogue.data === undefined) {
    return (
      <Panel caption={t('customer_manager_360.service_level.caption')} padded testId={testId}>
        <div
          data-testid={`${testId}-error`}
          role="alert"
          className="flex flex-col items-start gap-3 rounded-lg border border-bad-line bg-bad-dim px-4 py-3"
        >
          <p className="m-0 text-dense text-bad-bright">
            {t('customer_manager_360.service_level.error')}
          </p>
          <Button
            variant="secondary"
            size="sm"
            loading={assignment.isFetching || catalogue.isFetching}
            onClick={() => {
              void assignment.refetch();
              void catalogue.refetch();
            }}
            data-testid={`${testId}-retry`}
          >
            {t('customer_manager_360.service_level.retry')}
          </Button>
        </div>
      </Panel>
    );
  }

  const view = assignment.data;
  const isCustom = view.state === 'custom';

  // Read-only is the CUSTOMER MANAGER's rule, not everyone's. An org an admin
  // assigned a routing config directly reads back as `custom`, and the only
  // way out of that state — onto a level, or back to plain default — is a
  // write this card makes. Locking admins out too would make `custom` a
  // one-way door, since the routing page's assign dialog cannot clear it.
  if (isCustom && !isAdmin) {
    return (
      <Panel caption={t('customer_manager_360.service_level.caption')} padded testId={testId}>
        <div className="flex flex-col items-start gap-2" data-testid={`${testId}-custom`}>
          <Chip tone="signal">{t('customer_manager_360.service_level.custom_label')}</Chip>
          <p className="m-0 text-dense text-paper-low">
            {t('customer_manager_360.service_level.custom_hint')}
          </p>
        </div>
      </Panel>
    );
  }

  const active = catalogue.data.filter((level) => level.status === 'active');
  const options: SelectOption[] = [
    { value: DEFAULT_OPTION, label: t('customer_manager_360.service_level.default_option') },
    ...active.map((level) => ({ value: level.id, label: level.name })),
  ];
  // An archived level keeps its members, so a customer can still SIT on one.
  // It renders (otherwise the trigger goes blank) but stays unselectable, so
  // it is never an option the CM can move another customer onto.
  const assigned = view.level;
  if (assigned !== null && !active.some((level) => level.id === assigned.id)) {
    options.push({
      value: assigned.id,
      label: t('customer_manager_360.service_level.archived_option', { name: assigned.name }),
      disabled: true,
    });
  }

  const selectedLevel = selection === DEFAULT_OPTION
    ? null
    : active.find((level) => level.id === selection) ?? (assigned?.id === selection ? assigned : null);
  const description = selection === DEFAULT_OPTION
    ? t('customer_manager_360.service_level.default_description')
    : selectedLevel?.description ?? t('customer_manager_360.service_level.no_description');

  function handleSave(): void {
    setSaveError(null);
    setMut.mutate(
      { orgId, levelId: selection === DEFAULT_OPTION ? null : selection },
      {
        onSuccess: () => {
          // The mutation response is written straight into the assignment
          // cache, so releasing the draft shows the saved value, not the old
          // one, while the invalidated read catches up.
          setDraft(null);
          toast.success(t('customer_manager_360.service_level.saved'));
        },
        onError: (cause) => {
          // The draft is deliberately KEPT. The error text plus the unchanged
          // "currently:" line already say it did not save; throwing away the
          // selection would only force the CM to reopen the dropdown and pick
          // the same level again before they could retry.
          const message = t(saveErrorKey(cause));
          setSaveError(message);
          toast.error(message);
        },
      },
    );
  }

  /**
   * Clearing a `custom` org is the one transition on this card that cannot be
   * taken back here: this card never learns the routing config that was
   * assigned, so it cannot offer it again. Undoing it means reading the
   * original assignment out of the audit log and re-assigning it on the
   * tenant-routing page — recoverable, but archaeology. Every other move here
   * is reversed by picking again, so only this one is confirmed.
   */
  function requestSave(): void {
    if (setMut.isPending) return;
    if (isCustom && selection === DEFAULT_OPTION) {
      setConfirmClear(true);
      return;
    }
    handleSave();
  }

  return (
    <Panel caption={t('customer_manager_360.service_level.caption')} padded testId={testId}>
      <div className="flex flex-col gap-3">
        {isCustom ? (
          <div className="flex flex-col items-start gap-1" data-testid={`${testId}-custom-admin`}>
            <Chip tone="signal">{t('customer_manager_360.service_level.custom_label')}</Chip>
            <p className="m-0 text-dense text-paper-low">
              {t('customer_manager_360.service_level.custom_admin_hint')}
            </p>
          </div>
        ) : null}

        <div className="flex flex-wrap items-center gap-2">
          <div className="w-full max-w-sm">
            <Select
              id={`${testId}-select`}
              value={selection}
              onValueChange={(next) => {
                setDraft(next);
                setSaveError(null);
              }}
              options={options}
              ariaLabel={t('customer_manager_360.service_level.caption')}
              testId={`${testId}-select`}
              disabled={setMut.isPending}
            />
          </div>
          <Button
            variant="primary"
            size="sm"
            loading={setMut.isPending}
            // In `custom` the stored value is not the `default` sentinel the
            // control shows, so equality with `current` would disable the only
            // control that can hand the org back to the catalogue.
            disabled={!isCustom && selection === current}
            onClick={requestSave}
            data-testid={`${testId}-save`}
          >
            {t('customer_manager_360.service_level.save')}
          </Button>
        </div>

        <p className="m-0 text-dense text-paper-low" data-testid={`${testId}-description`}>
          {description}
        </p>

        {isCustom ? (
          <p className="m-0 text-micro text-paper-low" data-testid={`${testId}-state`}>
            {t('customer_manager_360.service_level.state_custom')}
          </p>
        ) : current === DEFAULT_OPTION ? (
          <p className="m-0 text-micro text-paper-low" data-testid={`${testId}-state`}>
            {t('customer_manager_360.service_level.state_default')}
          </p>
        ) : (
          <p className="m-0 text-micro text-paper-low" data-testid={`${testId}-state`}>
            {t('customer_manager_360.service_level.state_level', {
              name: assigned?.name ?? '',
            })}
          </p>
        )}

        {saveError !== null ? (
          <p
            className="m-0 rounded border border-bad-line bg-bad-dim px-3 py-2 text-dense text-bad-bright"
            role="alert"
            data-testid={`${testId}-save-error`}
          >
            {saveError}
          </p>
        ) : null}
      </div>

      <ConfirmDialog
        open={confirmClear}
        onOpenChange={(open) => {
          if (!open && !setMut.isPending) setConfirmClear(false);
        }}
        title={t('customer_manager_360.service_level.clear_modal.title')}
        body={t('customer_manager_360.service_level.clear_modal.body')}
        confirmLabel={t('customer_manager_360.service_level.clear_modal.confirm')}
        cancelLabel={t('common.cancel')}
        closeLabel={t('common.close')}
        onConfirm={() => {
          setConfirmClear(false);
          handleSave();
        }}
        tone="danger"
        loading={setMut.isPending}
        testId={`${testId}-clear`}
      />
    </Panel>
  );
}
